'use client';

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult, BrowserFilterConfig } from '@/components/ObjectBrowser';
import { ContentCard } from '@/components/content/ContentCard';
import type { ContentCardProps } from '@/components/content/ContentCard';
import { PlaybookCard } from '@/components/spark/PlaybookCard';
import { contentApi } from '@/lib/api';
import type { ApiContent, ContentQuery } from '@/lib/api/types';
import { CONTENT_TYPE_CONFIG } from '@sizzlenz/shared';
import type { SparkPlaybookCard } from '@sizzlenz/shared';

// --- Helpers ---

/** Fisher-Yates shuffle – returns a new array in random order. */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// --- Filter configs ---

const CONTENT_TYPE_FILTER: BrowserFilterConfig = {
  key: 'type',
  label: 'Type',
  options: Object.entries(CONTENT_TYPE_CONFIG).map(([key, config]) => ({
    id: key,
    label: config.label,
  })),
};

// --- Type-specific card helpers ---

function contentToPlaybookCard(content: ApiContent): SparkPlaybookCard {
  try {
    if (!content.content) throw new Error('No content');
    const cleaned = content.content.replace(/\n/g, ' ').replace(/\r/g, '');
    const parsed = JSON.parse(cleaned);
    return {
      iconName: parsed.iconName || 'BookOpen',
      title: parsed.title || content.title || 'Untitled',
      description: parsed.description || content.blurb || '',
      goals: parsed.outcomes || parsed.goals || [],
      effort: parsed.effort || '',
      budget: parsed.budget || '',
      businessTypes: parsed.businessTypes || [],
      phases: (parsed.timeline || []).map((m: any) => m.title),
      outcome: (parsed.outcomes || []).join(' + ') || '',
      color: 'bg-primary-100 text-primary-600',
      borderColor: 'border-primary-200',
    };
  } catch {
    return {
      iconName: 'BookOpen',
      title: content.title || 'Untitled',
      description: content.blurb || '',
      goals: [],
      effort: '',
      budget: '',
      businessTypes: [],
      phases: [],
      outcome: '',
      color: 'bg-primary-100 text-primary-600',
      borderColor: 'border-primary-200',
    };
  }
}

// --- Props ---

export interface ContentBrowserProps {
  /** Base query filters applied to every fetch (e.g. ownerType, ownerId, type). */
  query?: ContentQuery;
  /** Override the filter configs shown in the sidebar. */
  filters?: BrowserFilterConfig[];
  /** Enable search input. Defaults to true. */
  searchEnabled?: boolean;
  /** Search placeholder text. */
  searchPlaceholder?: string;
  /** Page size. Defaults to 20. */
  limit?: number;
  /** Show the filter panel (desktop sidebar + mobile overlay). Defaults to true. */
  showFilters?: boolean;
  /** Show type filter. Defaults to true. */
  showTypeFilter?: boolean;
  /** Empty state messaging. */
  emptyState?: { title: string; description: string; compact?: boolean };
  /** Custom card href builder. Receives the content item, returns a URL string. */
  getHref?: (content: ApiContent) => string;
  /** Label for the "Back to..." link on the detail page. Passed as query param via ContentCard. */
  backLabel?: string;
  /** URL for the "Back to..." link on the detail page. Passed as query param via ContentCard. */
  backHref?: string;
  /** Initial search value (for URL sync). */
  initialSearch?: string;
  /** Initial filter values (for URL sync). */
  initialFilters?: Record<string, string[]>;
  /** Called when search changes (for URL sync). */
  onSearchChange?: (search: string) => void;
  /** Called when filters change (for URL sync). */
  onFiltersChange?: (filters: Record<string, string[]>) => void;
  /** Show the result count header. Defaults to true. */
  showResultCount?: boolean;
  /** Force the filter layout mode. 'desktop' = always sidebar, 'mobile' = always overlay, 'auto' = responsive (default). */
  filterLayout?: 'desktop' | 'mobile' | 'auto';
  /** Display view mode. 'grid' (default) or 'list'. */
  viewMode?: 'grid' | 'list';
  /** Called when the user toggles the view mode. */
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  /** Show the grid/list view mode toggle buttons. Defaults to true. */
  showViewModeToggle?: boolean;
  /** Hide the results header (count + view toggle) when there are no results. Defaults to false. */
  hideHeaderOnEmpty?: boolean;
  /** Control the minimum height behavior. 'full' (default) uses viewport-based min-heights. 'none' removes all min-height constraints for embedded usage. */
  minHeight?: 'full' | 'none' | string;
  /** Custom list item renderer. Used in list mode. Falls back to ContentCard if not provided. */
  renderListItem?: (content: ApiContent) => React.ReactNode;
  /** Scroll to top of the page when search or filters change. Defaults to true. Set to false for embedded usage. */
  scrollOnChange?: boolean;
  /** Extra props forwarded to each rendered ContentCard (e.g. `showTypeBadge`, `showTags`, `showWhen`, `showImage`, `variant`). */
  cardProps?: Pick<
    ContentCardProps,
    'variant' | 'showTypeBadge' | 'showTags' | 'showWhen' | 'showImage'
  >;
  /** When this value changes, a refetch is triggered. Useful for external state changes. */
  fetchKey?: string;
  /** Randomly shuffle items within each fetched page. Defaults to false. */
  randomize?: boolean;
  /** Number of grid columns on the smallest (mobile) breakpoint. Defaults to 2. */
  mobileColumns?: 1 | 2;
}

export function ContentBrowser({
  query: baseQuery,
  filters,
  searchEnabled = true,
  searchPlaceholder = 'Search content...',
  limit = 20,
  showFilters = true,
  showTypeFilter = true,
  emptyState = { title: 'No content found', description: 'Try adjusting your search or filters' },
  getHref,
  backLabel,
  backHref,
  initialSearch,
  initialFilters,
  onSearchChange,
  onFiltersChange,
  showResultCount,
  filterLayout,
  viewMode,
  onViewModeChange,
  showViewModeToggle,
  hideHeaderOnEmpty,
  minHeight,
  renderListItem,
  scrollOnChange,
  cardProps,
  fetchKey,
  randomize = false,
  mobileColumns,
}: ContentBrowserProps) {
  const queryClient = useQueryClient();

  const filterConfigs = !showFilters
    ? []
    : filters ?? (showTypeFilter ? [CONTENT_TYPE_FILTER] : []);

  const fetcher = useCallback(
    async (params: {
      search?: string;
      filters: Record<string, string[]>;
      cursor?: string;
      limit: number;
    }): Promise<BrowserFetcherResult<ApiContent>> => {
      const types = params.filters.type || [];
      const tags = params.filters.tags || [];

      // When the type filter is user-managed (shown in filter panel), an empty selection
      // means "all types" — don't fall back to baseQuery.type in that case.
      const hasTypeFilter = filterConfigs.some((f) => f.key === 'type');
      const resolvedType = types.length > 0 ? types : hasTypeFilter ? undefined : baseQuery?.type;

      // Merge tags from filter panel with tags from baseQuery
      const resolvedTags =
        tags.length > 0
          ? tags
          : baseQuery?.tags && baseQuery.tags.length > 0
            ? baseQuery.tags
            : undefined;

      const fetchQuery: ContentQuery = {
        ...baseQuery,
        search: params.search || undefined,
        type: resolvedType,
        tags: resolvedTags,
        limit: params.limit,
        nextCursor: params.cursor || undefined,
      };

      const response = await queryClient.fetchQuery({
        queryKey: ['content', fetchQuery],
        queryFn: () => contentApi.getContent(fetchQuery),
        staleTime: 1000 * 10,
      });

      return {
        objects: randomize ? shuffleArray(response.content) : response.content,
        hasNextPage: !!response.pagination.nextCursor,
        nextCursor: response.pagination.nextCursor ?? undefined,
      };
    },
    [baseQuery, queryClient, filterConfigs, randomize]
  );

  return (
    <ObjectBrowser<ApiContent>
      fetcher={fetcher}
      fetchKey={fetchKey}
      renderCard={(content) => {
        if (content.type === 'playbook') {
          return (
            <Link
              href={getHref?.(content) || `/playbooks/${content.slug || content.id}`}
              className="cursor-pointer"
            >
              <PlaybookCard playbook={contentToPlaybookCard(content)} compact />
            </Link>
          );
        }
        return (
          <ContentCard
            content={content}
            variant="compact"
            href={getHref?.(content)}
            backLabel={backLabel}
            backHref={backHref}
            {...cardProps}
          />
        );
      }}
      getKey={(content) => content.id}
      filters={filterConfigs}
      searchEnabled={searchEnabled}
      searchPlaceholder={searchPlaceholder}
      limit={limit}
      emptyState={emptyState}
      initialSearch={initialSearch}
      initialFilters={initialFilters}
      onSearchChange={onSearchChange}
      onFiltersChange={onFiltersChange}
      showResultCount={showResultCount}
      filterLayout={filterLayout}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      showViewModeToggle={showViewModeToggle}
      hideHeaderOnEmpty={hideHeaderOnEmpty}
      minHeight={minHeight}
      renderListItem={renderListItem}
      scrollOnChange={scrollOnChange}
      mobileColumns={mobileColumns}
    />
  );
}
